import { useI18n } from '~/common/context';
import { CenteredSpinner } from '@wyz/ui';
import { mapIntoOptionItem } from '@wyz/utils';
import React, { useMemo } from 'react';
import {
  useManageBrands,
  useManageEnums,
  useManageParameters,
} from '~/common/api';
import {
  PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP,
  PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS,
} from '~/common/translators';
import {
  ISupplierTypeOptionItem,
  IVehicleTypeGroupsOptionItem,
} from '~/common/types';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';

// eslint-disable-next-line @wyz/module/no-relative-import
import { FormContext, IFormContext } from './contexts';
import { useMyCustomers } from '~/common/hooks';
import { useActiveVehicleTypeGroups } from '~/purchase/hooks';

export function CommonPricingPolicyFormProvider({
  children,
  mode,
}: React.PropsWithChildren<Pick<IFormContext, 'mode'>>) {
  const { t } = useI18n();

  const manageBrands = useManageBrands({
    paginate: {
      enabled: true,
      args: {
        brandPublicKeys: [],
      },
    },
  });
  const manageEnums = useManageEnums({
    getSupplierTypes: { enabled: true, args: { scope: 'PRICING_POLICY' } },
  });

  const manageParameters = useManageParameters({
    getSeasons: { enabled: true },
  });

  const getCustomersByUser = useMyCustomers();

  const activeVehicleTypeGroups = useActiveVehicleTypeGroups();

  const context: IFormContext = useMemo(() => {
    return {
      mode,
      vehicleTypeGroups: {
        ...activeVehicleTypeGroups,
        optionItems: mapIntoOptionItem(
          activeVehicleTypeGroups.data?.data ?? [],
          (item) => t(item.i18nKey, { defaultValue: item.name }),
          'publicKey',
          (item) => ({ name: item.name }),
        ) as Array<IVehicleTypeGroupsOptionItem>,
      },
      seasons: {
        ...manageParameters.getSeasons,
        optionItems: mapIntoOptionItem(
          manageParameters.getSeasons.data?.data ?? [],
          (season) => t(season.i18nKey),
          'id',
        ),
      },
      pricingConfigurationTypes: {
        optionItems: Object.values(PRICING_TYPE_ENUM).map((pricingType) => ({
          value: pricingType,
          label: t(PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP[pricingType]),
        })),
      },
      supplierTypes: mapIntoOptionItem(
        manageEnums.getSupplierTypes.query.data?.data ?? [],
        (item) => t(PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS[item.value]),
        'codename',
        (item) => ({ id: item.value }),
      ) as Array<ISupplierTypeOptionItem>,
      brands: {
        all: manageBrands.paginate.data?.data ?? [],
      },
    } satisfies IFormContext;
  }, [
    mode,
    activeVehicleTypeGroups,
    manageParameters.getSeasons,
    manageBrands.paginate.data?.data,
    t,
    manageEnums.getSupplierTypes.query.data?.data,
  ]);

  const requiredQueries = [
    getCustomersByUser,
    manageEnums.getSupplierTypes.query,
    manageParameters.getSeasons,
    manageBrands.paginate,
    activeVehicleTypeGroups,
  ];
  return (
    <FormContext.Provider value={context}>
      {requiredQueries.some((query) => !query.isSuccess) && <CenteredSpinner />}
      {requiredQueries.every((query) => query.isSuccess) && children}
    </FormContext.Provider>
  );
}
export const usePricingPolicyForm = () => {
  const context = React.useContext(FormContext);
  if (context === undefined) {
    throw new Error('usePricingPolicyForm must be used within a FormContext');
  }
  return context;
};
